import { InternalId } from "@sp-crm/core";
import { SupportEmailLink } from "components/app/support-email-link";
import { Alert } from "components/ui/dialog/alert";
import * as React from "react";
import { useCallback } from "react";
import { useAppDispatch, useAppSelector } from "store/hooks";
import {
    makeRequestFailAcknowledgeAction,
    makeRequestUnauthorizedAcknowledgeAction,
} from "../../store/actions/types";
import { RequestDescription } from "../../types/request-description";
import { GlobalSuccess } from "./global-success";

interface RequestAlertProps {
    request: RequestDescription;
    onDismiss: () => void;
}

const FailureAlert: React.FC<RequestAlertProps> = ({ request, onDismiss }) => {
    const subject = request.correlationId || "UnknownError";

    return (
        <Alert title="Sorry, something went wrong" onDismiss={onDismiss}>
            <div>
                <p>We&apos;re sorry, something went wrong.</p>
                <br />
                <p>
                    If you were in the middle of updating data, please try entering it
                    again. If you see this error again, please contact us using the
                    information below.
                </p>
                <br />
                <p>
                    Email <SupportEmailLink subject={`Error report: ${subject}`} />, and
                    please include the information below:
                </p>
                <br />
                <div className="space-y-1">
                    <div className="font-bold">Diagnostic</div>
                    <div>
                        <code>{request.correlationId}</code>
                    </div>
                </div>
            </div>
        </Alert>
    );
};

const UnauthorizedAlert: React.FC<RequestAlertProps> = ({ request, onDismiss }) => {
    const subject = request.correlationId || "UnknownError";

    return (
        <Alert onDismiss={onDismiss} title="Sorry, access denied">
            <div>
                <p>We&apos;re sorry, doesn&apos;t look like you have access to that.</p>
                <br />
                <p>
                    If you believe this to be in error, please contact us using the
                    information below.
                </p>
                <br />
                <p>
                    Email <SupportEmailLink subject={`Error report: ${subject}`} />, and
                    please include the information below:
                </p>
                <br />
                <div className="space-y-1">
                    <div className="font-bold">Diagnostic</div>
                    <div>
                        <code>{request.correlationId}</code>
                    </div>
                </div>
            </div>
        </Alert>
    );
};

export const Alerts: React.FC = () => {
    const dispatch = useAppDispatch();
    const failures = useAppSelector(state => state.globalNotifications.faildRequests);
    const unauthorized = useAppSelector(
        state => state.globalNotifications.unauthorizedRequests,
    );

    const clearAlert = useCallback(
        (eventId: InternalId) => {
            dispatch(makeRequestFailAcknowledgeAction(eventId));
            dispatch(makeRequestUnauthorizedAcknowledgeAction(eventId));
        },
        [dispatch],
    );

    const firstFailure = Object.values(failures)[0];
    if (firstFailure) {
        return (
            <FailureAlert
                request={firstFailure}
                onDismiss={() => clearAlert(firstFailure.correlationId as InternalId)}
            />
        );
    }

    const firstUnauthorized = Object.values(unauthorized)[0];
    if (firstUnauthorized) {
        return (
            <UnauthorizedAlert
                request={firstUnauthorized}
                onDismiss={() =>
                    clearAlert(firstUnauthorized.correlationId as InternalId)
                }
            />
        );
    }

    return <GlobalSuccess />;
};
